import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { Button, Input, DatePicker, Select } from '@skbkontur/react-ui';
import { Add, ArrowBoldLeft } from '@skbkontur/react-icons';
import { MainApi } from '../../utils/MainApi'
import "./Form.css";
import FormRow from "../../components/FormRow/FormRow";
import ProductPopup from "../../components/ProductPopup/ProductPopup";
import SavedListsAccordion from "../../components/SavedListsAccordion/SavedListsAccordion";
import Switcher from "../../components/Switcher/Switcher";
import ContractorPopup from "../../components/ContractorPopup/ContractorPopup";
import EventPopup from "../../components/EventPopup/EventPopup";
import useKpStore from '../../hooks/useKpStore';
import PageContainer from "../../components/Layout/PageContainer";
import PageHeader from "../../components/Layout/PageHeader";

function Form({
  onSubmit,
  kpNumber,
  formInfo,
  // addList,
  listsSummary,
  dateToISO,
  getProductWeightWithMeasure,
  isNewKp,
  onDeleteRow,
  onUpdateRow,
  onAddRowOnList,
  // onDeleteList,
}) {
  const { formData, setFormData, updateFields, updateField, addList } = useKpStore();
  const listsKp = useKpStore((s) => s.listsKp);
  const [errors, setErrors] = useState({
    kpNumber: '',
    kpDate: '',
    contractNumber: '',
    contractDate: '',
    startEvent: '',
    endEvent: '',
    startTimeStartEvent: '',
    endTimeStartEvent: '',
    startTimeEndEvent: '',
    endTimeEndEvent: '',
    eventPlace: '',
    countOfPerson: '',
    logisticsCost: '',
    listTitle: '',
    _lists: '',
  });

  const navigate = useNavigate();
  const [productToEdit, setProductToEdit] = useState(null);
  // Состояние для списка позиций КП (таблица)
  const [products, setProducts] = useState([]);
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [showContractorPopup, setShowContractorPopup] = useState(false);
  const [showEventPopup, setShowEventPopup] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    MainApi.getContractors()
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          setContractors(data);
        }
      })
      .catch((err) => console.error("Ошибка загрузки контрагентов:", err));

    MainApi.getEvents()
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          // Filter for upcoming events (not Completed/Cancelled)
          const upcoming = data.filter(e => !['Completed', 'Cancelled'].includes(e.status));
          setEvents(upcoming);
        }
      })
      .catch((err) => console.error("Ошибка загрузки событий:", err));

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isNewKp) return;
    let cancelled = false;

    MainApi.getLastKpNumber()
      .then((res) => {
        if (cancelled) return;

        // бэк возвращает, например, "9"
        const last = parseInt(String(res).trim(), 10);
        const next = Number.isFinite(last) ? last + 1 : 1;

        updateField('kpNumber', String(next)); // заменили setFormData на updateField
        updateField('contractNumber', String(next));
        setErrors(prev => ({ ...prev, kpNumber: '' }));
      })
      .catch((err) => {
        console.error('Ошибка получения следующего номера КП:', err);
        updateField('kpNumber', '1');
      });

    return () => { cancelled = true; };
  }, [isNewKp, updateField]);

  useEffect(() => {
    if (kpNumber != null && kpNumber !== '' && kpNumber !== formData.kpNumber) {
      updateField('kpNumber', kpNumber);
      setErrors(prev => ({ ...prev, kpNumber: '' }));
    }
  }, [kpNumber, formData.kpNumber, updateField]);

  const isValidDate = (v) => /^\d{2}\.\d{2}\.\d{4}$/.test(v) || /^\d{4}-\d{2}-\d{2}$/.test(v);
  const isValidTime = (v) => /^\d{2}:\d{2}$/.test(v) && +v.slice(0, 2) < 24 && +v.slice(3, 5) < 60;
  const isPositiveInt = (v) => /^\d+$/.test(String(v)) && +v >= 1 && +v <= 99999;
  const isNonNegativeNumber = (v) => /^-?\d+(\.\d+)?$/.test(String(v)) && +v >= 0;
  const lenBetween = (s, min, max) => (s ?? '').toString().length >= min && (s ?? '').toString().length <= max;

  // Валидация одного поля
  const validateField = (name, value, all) => {
    switch (name) {
      case 'kpNumber':
        if (!String(value).trim()) return 'Обязательное поле';
        if (!/^\d{1,10}$/.test(String(value))) return 'Только цифры, 1–10 символов';
        return '';
      case 'kpDate':
        if (!isValidDate(value)) return 'Введите дату в формате DD.MM.YYYY';
        return '';
      case 'contractNumber':
        if (!String(value).trim()) return 'Обязательное поле';
        if (!lenBetween(value, 1, 30)) return 'До 30 символов';
        return '';
      case 'contractDate':
        if (!isValidDate(value)) return 'Введите дату в формате DD.MM.YYYY';
        return '';
      case 'startEvent':
        if (!isValidDate(value)) return 'Введите дату в формате DD.MM.YYYY';
        if (all?.endEvent && dateToISO(value) && dateToISO(all.endEvent) && dateToISO(value) > dateToISO(all.endEvent))
          return 'Дата начала позже даты окончания';
        return '';
      case 'endEvent':
        if (!isValidDate(value)) return 'Введите дату в формате DD.MM.YYYY';
        if (all?.startEvent && dateToISO(value) && dateToISO(all.startEvent) && dateToISO(all.startEvent) > dateToISO(value))
          return 'Дата окончания раньше даты начала';
        return '';
      case 'startTimeStartEvent':
      case 'endTimeStartEvent':
      case 'startTimeEndEvent':
      case 'endTimeEndEvent':
        if (!isValidTime(value)) return 'Формат HH:MM';
        return '';
      case 'eventPlace':
        if (!String(value).trim()) return 'Обязательное поле';
        if (!lenBetween(value, 2, 120)) return '2–120 символов';
        return '';
      case 'countOfPerson':
        if (!isPositiveInt(value)) return 'Целое число 1…99999';
        return '';
      case 'logisticsCost':
        if (!isNonNegativeNumber(value)) return 'Число ≥ 0';
        return '';
      case 'listTitle':
        if (!String(value).trim()) return 'Обязательное поле';
        if (!lenBetween(value, 2, 80)) return '2–80 символов';
        return '';
      default:
        return '';
    }
  };

  // Полная проверка формы + листов (на submit)
  const validateForm = (all, lists) => {
    const next = { ...errors, _lists: '' };

    // поля формы
    Object.keys(next).forEach(k => {
      if (k === '_lists') return;
      next[k] = validateField(k, all[k], all);
    });

    // листы/позиции
    if (!Array.isArray(lists) || lists.length === 0) {
      next._lists = 'Добавьте хотя бы один лист';
    } else if (lists.some(l => !Array.isArray(l.rows) || l.rows.length === 0)) {
      next._lists = 'В каждом листе должна быть хотя бы одна позиция';
    }

    const isValid = Object.values(next).every(v => !v);
    return { next, isValid };
  };

  const isFormValidNow = (() => {
    // пробегаемся по полям без setErrors
    const fieldsOk = Object.keys(errors).every(k => k === '_lists' || !errors[k]) &&
      validateField('kpNumber', formData.kpNumber, formData) === '' &&
      validateField('kpDate', formData.kpDate, formData) === '' &&
      validateField('contractNumber', formData.contractNumber, formData) === '' &&
      validateField('contractDate', formData.contractDate, formData) === '' &&
      validateField('startEvent', formData.startEvent, formData) === '' &&
      validateField('endEvent', formData.endEvent, formData) === '' &&
      validateField('startTimeStartEvent', formData.startTimeStartEvent, formData) === '' &&
      validateField('endTimeStartEvent', formData.endTimeStartEvent, formData) === '' &&
      validateField('startTimeEndEvent', formData.startTimeEndEvent, formData) === '' &&
      validateField('endTimeEndEvent', formData.endTimeEndEvent, formData) === '' &&
      validateField('eventPlace', formData.eventPlace, formData) === '' &&
      validateField('countOfPerson', formData.countOfPerson, formData) === '' &&
      validateField('logisticsCost', formData.logisticsCost, formData) === '' &&
      validateField('listTitle', formData.listTitle, formData) === '';
    const listsOk = Array.isArray(listsSummary) &&
      listsSummary.length > 0 &&
      listsSummary.every(l => Array.isArray(l.rows) && l.rows.length > 0);
    return fieldsOk && listsOk;
  })();

  // мгновенная валидация при изменении поля
  // const handleValidatedChange = (name, value) => {
  //   setFormData(prev => {
  //     const updated = { ...prev, [name]: value };
  //     const msg = validateField(name, value, updated);
  //     setErrors(e => ({ ...e, [name]: msg }));
  //     return updated;
  //   });
  // };
  const handleValidatedChange = (name, value) => {
    const msg = validateField(name, value, { ...formData, [name]: value });
    setErrors(e => ({ ...e, [name]: msg }));
    updateField(name, value); // заменили setFormData на updateField
  };



  const handleEditProduct = useCallback((product) => {
    setProductToEdit(product);
    setShowProductPopup(true);
  }, []);

  const handleUpdateProduct = useCallback((updatedProduct) => {
    setProducts(prev => prev.map(item =>
      item.id === updatedProduct.id
        ? {
          ...item,           // Сохраняем неизмененные поля
          ...updatedProduct  // Обновляем измененные поля
        }
        : item
    ));
    setProductToEdit(null);
    setShowProductPopup(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target ?? e;
    handleValidatedChange(name, value);
  }, []); // eslint-disable-line

  // Добавление новой позиции в таблицу (при сохранении всплывающего окна)
  const handleAddProduct = (product) => {
    // Присваиваем временный ID новой позиции и добавляем в список
    const newItem = { id: Date.now(), ...product };
    setProducts(prev => [...prev, newItem]);
    setShowProductPopup(false);
  };

  // Удаление позиции из списка по ID
  const handleRemoveProduct = useCallback((id) => {
    setProducts(prev => prev.filter(item => item.id !== id));
  }, []);



  const handleEventSelect = useCallback((e) => {
    const selectedId = e.target.value ? Number(e.target.value) : null;

    if (selectedId) {
      const selectedEvent = events.find(ev => ev.id === selectedId);
      if (selectedEvent) {
        const newFields = { eventId: selectedId };
        if (selectedEvent.title) newFields.listTitle = selectedEvent.title;
        if (selectedEvent.startEvent || selectedEvent.eventDate) newFields.startEvent = selectedEvent.startEvent || selectedEvent.eventDate;
        if (selectedEvent.endEvent || selectedEvent.eventDate) newFields.endEvent = selectedEvent.endEvent || selectedEvent.eventDate;
        if (selectedEvent.startTimeStartEvent || selectedEvent.startTime) newFields.startTimeStartEvent = (selectedEvent.startTimeStartEvent || selectedEvent.startTime).slice(0, 5);
        if (selectedEvent.endTimeStartEvent) newFields.endTimeStartEvent = selectedEvent.endTimeStartEvent.slice(0, 5);
        if (selectedEvent.startTimeEndEvent) newFields.startTimeEndEvent = selectedEvent.startTimeEndEvent.slice(0, 5);
        if (selectedEvent.endTimeEndEvent || selectedEvent.endTime) newFields.endTimeEndEvent = (selectedEvent.endTimeEndEvent || selectedEvent.endTime).slice(0, 5);
        if (selectedEvent.eventPlace) newFields.eventPlace = selectedEvent.eventPlace;
        if (selectedEvent.countOfPerson) newFields.countOfPerson = String(selectedEvent.countOfPerson);

        updateFields(newFields);
      }
    } else {
      updateField('eventId', null);
    }
  }, [events, updateFields, updateField]);

  const handleCreateContractor = async (contractorData) => {
    try {
      const createdContractor = await MainApi.createContractor(contractorData);
      setContractors(prev => [...prev, createdContractor]);
      updateField('contractorId', createdContractor.id);
      setShowContractorPopup(false);
    } catch (err) {
      console.error('Ошибка при создании контрагента:', err);
      alert('Не удалось создать контрагента. Проверьте консоль.');
    }
  };

  const handleCreateEvent = async (eventData) => {
    try {
      const createdEvent = await MainApi.createEvent(eventData);
      setEvents(prev => [...prev, createdEvent]);
      
      // Auto-populate KP form data
      const newFields = { eventId: createdEvent.id };
      if (createdEvent.title) newFields.listTitle = createdEvent.title;
      if (createdEvent.startEvent || createdEvent.eventDate) newFields.startEvent = createdEvent.startEvent || createdEvent.eventDate;
      if (createdEvent.endEvent || createdEvent.eventDate) newFields.endEvent = createdEvent.endEvent || createdEvent.eventDate;
      if (createdEvent.startTimeStartEvent || createdEvent.startTime) newFields.startTimeStartEvent = (createdEvent.startTimeStartEvent || createdEvent.startTime).slice(0, 5);
      if (createdEvent.endTimeStartEvent) newFields.endTimeStartEvent = createdEvent.endTimeStartEvent.slice(0, 5);
      if (createdEvent.startTimeEndEvent) newFields.startTimeEndEvent = createdEvent.startTimeEndEvent.slice(0, 5);
      if (createdEvent.endTimeEndEvent || createdEvent.endTime) newFields.endTimeEndEvent = (createdEvent.endTimeEndEvent || createdEvent.endTime).slice(0, 5);
      if (createdEvent.eventPlace) newFields.eventPlace = createdEvent.eventPlace;
      if (createdEvent.countOfPerson) newFields.countOfPerson = String(createdEvent.countOfPerson);
      
      updateFields(newFields);
      setShowEventPopup(false);
    } catch (err) {
      console.error('Ошибка при создании события:', err);
      alert('Не удалось создать событие. Проверьте консоль.');
    }
  };

  // Отправка формы (создание нового КП)
  const handleSubmit = (e) => {
    e.preventDefault();
    const { next, isValid } = validateForm(formData, listsSummary);
    setErrors(next);
    if (!isValid) {
      // можно пробежаться и сфокусировать первое ошибочное поле, если нужно
      alert('Заполните обязательные поля корректно');
      return;
    }

    if (typeof onSubmit === 'function') {
      onSubmit(formData, listsKp);
    } else {
      console.error('onSubmit не передан в Form');
    }
  };
  function toDDMMYYYY(input) {
    if (!input) return '';

    // Если это объект Date
    if (input instanceof Date && !isNaN(input)) {
      const day = String(input.getDate()).padStart(2, '0');
      const month = String(input.getMonth() + 1).padStart(2, '0');
      const year = input.getFullYear();
      return `${day}.${month}.${year}`;
    }

    // Если строка YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      const [year, month, day] = input.split('-');
      return `${day}.${month}.${year}`;
    }

    // Если уже DD.MM.YYYY
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(input)) {
      return input;
    }

    return '';
  }

  return (
    <PageContainer maxWidth="1000px">
      <PageHeader
        title={isNewKp ? "Новое коммерческое предложение" : "Редактирование коммерческого предложения"}
        subtitle="Заполните основные данные, сведения о мероприятии, логистике и добавьте позиции КП"
        actions={
          <Button
            use="default"
            icon={<ArrowBoldLeft />}
            onClick={() => navigate('/')}
          >
            На главную
          </Button>
        }
      />
      <form className="form" onSubmit={handleSubmit}>

        {/* Секция: Основная информация */}
        <div className="form__section">
          <h3 className="form__section-title">Основная информация</h3>
          <div className="form__fields">
            <div className="form__field">
              <label className="form__label" htmlFor="kpNumber">Номер коммерческого предложения</label>
              <Input
                width="100%"
                id="kpNumber"
                name="kpNumber"
                data-testid="kp-number"
                className={`form__input ${errors.kpNumber ? 'error' : ''}`}
                value={(isNewKp) ? formData.kpNumber : formInfo.kpNumber}
                onValueChange={value => handleInputChange({ target: { name: 'kpNumber', value } })}
              />
            </div>
            <div className="form__field">
              <label className="form__label" htmlFor="kpDate">Дата коммерческого предложения</label>
              <DatePicker
                id="kpDate"
                name="kpDate"
                data-testid="kp-date"
                className={`form__input ${errors.kpDate ? 'error' : ''}`}
                value={(isNewKp) ? toDDMMYYYY(formData.kpDate) : toDDMMYYYY(formInfo.kpDate)}
                onValueChange={value => handleInputChange({ target: { name: 'kpDate', value } })}
              />
            </div>
            <div className="form__field">
              <label className="form__label" htmlFor="contractNumber">№ договора</label>
              <Input
                width="100%"
                id="contractNumber"
                name="contractNumber"
                data-testid="kp-contract-number"
                className={`form__input ${errors.contractNumber ? 'error' : ''}`}
                value={formData.contractNumber}
                onValueChange={value => handleInputChange({ target: { name: 'contractNumber', value } })}
              />
            </div>
            <div className="form__field">
              <label className="form__label" htmlFor="contractDate">Дата договора</label>
              <DatePicker
                id="contractDate"
                name="contractDate"
                className={`form__input ${errors.contractDate ? 'error' : ''}`}
                value={(isNewKp) ? toDDMMYYYY(formData.contractDate) : toDDMMYYYY(formInfo.contractDate)}
                onValueChange={value => handleInputChange({ target: { name: 'contractDate', value } })}
              />
            </div>
          </div>
        </div>

        {/* Секция: Данные мероприятия */}
        <div className="form__section">
          <h3 className="form__section-title">Данные мероприятия</h3>
          <div className="form__fields">

            <div className="form__field">
              <label className="form__label" htmlFor="contractorId">Контрагент</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ flexGrow: 1 }}>
                  <Select
                    id="contractorId"
                    data-testid="kp-contractor-select"
                    items={['none', ...contractors.map(c => String(c.id))]}
                    value={formData.contractorId ? String(formData.contractorId) : 'none'}
                    onValueChange={value => handleInputChange({ target: { name: 'contractorId', value: value === 'none' ? null : Number(value) } })}
                    renderItem={item => {
                      if (item === 'none') return '— Не выбран —';
                      const match = contractors.find(c => String(c.id) === item);
                      return match ? match.companyName : item;
                    }}
                    renderValue={item => {
                      if (item === 'none') return '— Не выбран —';
                      const match = contractors.find(c => String(c.id) === item);
                      return match ? match.companyName : item;
                    }}
                    width="100%"
                  />
                </div>
                <span data-testid="add-contractor-btn">
                  <Button
                    use="default"
                    icon={<Add />}
                    onClick={() => setShowContractorPopup(true)}
                    title="Добавить контрагента"
                  />
                </span>
              </div>
            </div>

            <div className="form__field">
              <label className="form__label" htmlFor="eventId">Связь с событием</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ flexGrow: 1 }}>
                  <select
                    id="eventId"
                    data-testid="kp-event-select"
                    className="event-form__select"
                    value={formData.eventId || ''}
                    onChange={handleEventSelect}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', background: 'white' }}
                  >
                    <option value="">— Новое событие —</option>
                    {events.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.title} ({ev.eventDate || ev.startEvent})
                      </option>
                    ))}
                  </select>
                </div>
                <span data-testid="add-event-btn">
                  <Button
                    use="default"
                    icon={<Add />}
                    onClick={() => setShowEventPopup(true)}
                    title="Создать событие"
                  />
                </span>
              </div>
            </div>

          </div>
        </div>

        {/* Секция: Логистика */}
        <div className="form__section">
          <h3 className="form__section-title">Логистика</h3>
          <div className="form__fields form__field_logistic">
            <div className="form__field">
              <label className="form__label">В пределах МКАД?</label>
              <Switcher
                ariaLabel="В пределах МКАД"
                name="isWithinMkad"
                value={formData.isWithinMkad ? "true" : "false"}
                options={[
                  { value: "true", label: "Да" },
                  { value: "false", label: "Нет" },
                ]}
                onChange={(val) =>
                  handleInputChange({
                    target: { name: "isWithinMkad", value: val === "true" },
                  })
                }
              />
            </div>

            <div className="form__field">
              <label className="form__label" htmlFor="logisticsCost">
                Стоимость логистики
              </label>
              <Input
                width="100%"
                type="number"
                id="logisticsCost"
                name="logisticsCost"
                data-testid="kp-logistics"
                className={`form__input ${errors.logisticsCost ? 'error' : ''}`}
                value={formData.logisticsCost}
                onValueChange={(value) =>
                  handleInputChange({
                    target: { name: 'logisticsCost', value },
                  })
                }
              />
            </div>
          </div>
        </div>


        {/* Секция: Менеджер */}
        {/* <div className="form__section">
        <h3 className="form__section-title">Ответственный менеджер</h3>
        <div className="form__fields">
          <div className="form__field form__field--full">
            <Switcher
              ariaLabel="Ответственный менеджер"
              name="manager"
              value={formData.manager || 'peter'} // или как у вас хранится
              options={[
                { value: 'peter', label: "Пётр" },
                { value: 'pavel', label: 'Павел' },
              ]}
              onChange={handleManagerChange}
            />

          </div>
        </div>
      </div> */}

        {/* Секция: Состав КП (таблица с позициями) */}
        <div className="form__section" style={{ marginBottom: 0 }}>
          <h3 className="form__section-title">Состав КП</h3>
          {Array.isArray(listsSummary) && listsSummary.length > 0 && (
            <SavedListsAccordion
              lists={listsSummary}
              isNewKp={isNewKp}              // прокинем из App
              onDeleteRow={onDeleteRow}      // (listId, rowIndex)
              onUpdateRow={onUpdateRow}      // (listId, rowIndex, updatedRow)
              onAddRowOnList={onAddRowOnList}// (row, listId)
              // onDeleteList={onDeleteList}    // (listId)
              getProductWeightWithMeasure={getProductWeightWithMeasure}
            />
          )}
          <div className="form__table-container">

            {/* Ряды таблицы */}
            {products.map((prod) => (
              <FormRow
                key={prod.id}
                productData={prod}
                handleRemoveProduct={handleRemoveProduct}
                handleEditProduct={handleEditProduct}
                getProductWeightWithMeasure={getProductWeightWithMeasure}
              />
            ))}
            {errors._lists && (
              <div className="form__error" style={{ marginTop: 8 }}>{errors._lists}</div>
            )}
          </div>

          {/* Кнопки управления */}

          <label className="" style={{ display: "grid", "font-size": "12px" }}>
            <div className='form__table-button'>
              <Button
                icon={<Add />}
                use="default"
                data-testid="kp-add-row-button"
                onClick={() => {
                  setProductToEdit(null);
                  setShowProductPopup(true);
                  console.log(products);

                }}
                disabled={(products.length < 7) ? false : true}
              >
                Добавить позицию
              </Button>

              <Button
                use="primary"
                data-testid="kp-save-list-button"
                onClick={() => {
                  if (!products || products.length === 0) {
                    alert('Добавьте хотя бы одну позицию перед сохранением листа');
                    return;
                  }
                  if (typeof addList === 'function') {
                    addList(products);
                    console.log(listsKp);
                  }
                  setProducts([]);         // очистим текущий временный лист
                  setProductToEdit(null);  // сброс редактируемого продукта, если был
                }}
              >
                Сохранить лист
              </Button>
            </div>
            <span className="form__error" style={((products.length < 7) ? { color: '#ff000000' } : { color: '#d00', "text-align": "justify" })}>Достигнуто максимальное количество продуктов на одном листе</span>
          </label>


          {/* Попап */}
          {showProductPopup && (
            <ProductPopup
              onClose={() => {
                setProductToEdit(null);
                setShowProductPopup(false);
              }}
              onSave={productToEdit ? handleUpdateProduct : handleAddProduct}
              productToEdit={productToEdit}
            />
          )}

          {showContractorPopup && (
            <ContractorPopup
              onClose={() => setShowContractorPopup(false)}
              onSave={handleCreateContractor}
            />
          )}

          {showEventPopup && (
            <EventPopup
              onClose={() => setShowEventPopup(false)}
              onSave={handleCreateEvent}
              contractors={contractors}
              initialContractorId={formData.contractorId}
            />
          )}
        </div>

        {/* Кнопка сохранения формы */}
        <div className="form__actions">
          <Button use="success" type="submit" data-testid="kp-save-button" disabled={!isFormValidNow}>
            Сохранить коммерческое предложение
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}

export default Form;
